import { EquipmentCalculatorSectionKey } from "@/composables/scenarioCalculations/calculationTypes";
import {
  Severity,
  valuesExist,
  type ValidationRuleGenerator
} from "../scenarioValidator";
import { MIN_CLEARANCE } from "../validatorConstants";

export const tubingSizeChecksNames = [
  "top_tubing_size_top_casing",
  "top_tubing_size_bottom_casing",
  "bottom_tubing_size_top_casing",
  "bottom_tubing_size_bottom_casing",
  "tubing_length_check",
  "tubing_length_below_threshold"
] as const;

export const tubingSizeChecks: ValidationRuleGenerator = ({
  wellProperties,
  calcs
}) => [
  // Tubing size check - just need to check the bottom of top tubing since top of it is
  // at surface

  {
    name: "top_tubing_size_top_casing",
    message: `Clearance Error: Your top tubing is larger than your top casing`,
    severity: Severity.error,
    isActive: valuesExist([
      wellProperties.value?.r_top_casing?.drift_inches,
      wellProperties.value?.top_casing_length_feet,
      wellProperties.value?.r_top_tubing?.coupling_outer_diameter_inches,
      wellProperties.value?.top_tubing_length_feet
    ]),
    isInvalid:
      wellProperties.value?.r_top_casing?.drift_inches -
        wellProperties.value?.r_top_tubing?.coupling_outer_diameter_inches <
      MIN_CLEARANCE,
    rulePlacement: EquipmentCalculatorSectionKey.OPERATING_PARAMETERS
  },
  {
    name: "top_tubing_size_bottom_casing",
    message: `Clearance Error: Your top tubing is larger than your bottom casing`,
    severity: Severity.error,
    isActive:
      valuesExist([
        wellProperties.value?.r_bottom_casing?.drift_inches,
        wellProperties.value?.bottom_casing_length_feet,
        wellProperties.value?.r_top_tubing?.coupling_outer_diameter_inches,
        wellProperties.value?.top_tubing_length_feet
      ]) &&
      wellProperties.value?.top_tubing_length_feet >
        wellProperties.value?.top_casing_length_feet,
    isInvalid:
      wellProperties.value?.r_bottom_casing?.drift_inches -
        wellProperties.value?.r_top_tubing?.coupling_outer_diameter_inches <
      MIN_CLEARANCE,
    rulePlacement: EquipmentCalculatorSectionKey.OPERATING_PARAMETERS
  },
  {
    name: "bottom_tubing_size_top_casing",
    message: `Clearance Error: Your bottom tubing is larger than your top casing`,
    severity: Severity.error,
    isActive: valuesExist([
      wellProperties.value?.r_top_casing?.drift_inches,
      wellProperties.value?.top_casing_length_feet,
      wellProperties.value?.r_bottom_tubing?.coupling_outer_diameter_inches,
      wellProperties.value?.bottom_tubing_length_feet
    ]),
    isInvalid:
      wellProperties.value?.r_top_casing?.drift_inches -
        wellProperties.value?.r_bottom_tubing?.coupling_outer_diameter_inches <
      MIN_CLEARANCE,
    rulePlacement: EquipmentCalculatorSectionKey.OPERATING_PARAMETERS
  },
  {
    name: "bottom_tubing_size_bottom_casing",
    message: `Clearance Error: Your bottom tubing is larger than your bottom casing`,
    severity: Severity.error,
    isActive:
      valuesExist([
        wellProperties.value?.r_bottom_casing?.drift_inches,
        wellProperties.value?.bottom_casing_length_feet,
        wellProperties.value?.r_bottom_tubing?.coupling_outer_diameter_inches,
        wellProperties.value?.bottom_tubing_length_feet
      ]) &&
      wellProperties.value?.top_tubing_length_feet + 1 >
        wellProperties.value?.top_casing_length_feet,
    isInvalid:
      wellProperties.value?.r_bottom_casing?.drift_inches -
        wellProperties.value?.r_bottom_tubing?.coupling_outer_diameter_inches <
      MIN_CLEARANCE,
    rulePlacement: EquipmentCalculatorSectionKey.OPERATING_PARAMETERS
  },
  {
    name: "tubing_length_check",
    message: "Tubing length exceeds casing length",
    severity: Severity.error,
    isActive: valuesExist([
      wellProperties.value?.top_casing_id,
      wellProperties.value?.top_tubing_id,
      wellProperties.value?.top_casing_length_feet,
      wellProperties.value?.top_tubing_length_feet
    ]),
    isInvalid:
      calcs.equipmentLengths.tubingLengthFt.value >
      calcs.equipmentLengths.totalCasingLengthFt.value,
    disablesRules: [
      "top_tubing_size_top_casing",
      "bottom_tubing_size_bottom_casing"
    ],
    rulePlacement: EquipmentCalculatorSectionKey.OPERATING_PARAMETERS
  },
  {
    name: "tubing_length_below_threshold",
    message: "Requires Lock Plates (Tubing Length <= 2000 ft)",
    severity: Severity.error,
    isActive:
      wellProperties.value?.top_tubing_id && calcs.isScenarioEspShore.value,
    isInvalid: calcs.equipmentLengths.tubingLengthFt.value <= 2000,
    rulePlacement: EquipmentCalculatorSectionKey.OPERATING_PARAMETERS
  }
];
